/*
 * NLP.ts
 * ===========
 * Basic natural language handling utilities from text input.
 */
import nlp from "compromise";
import datePlugin from "compromise-dates";
import sentiment from "wink-sentiment";
import { SpeakerType } from "tsproto/aware_proto/proto/Interaction";
import DateUtils from "./DateUtils";
import HybridTextSearch from "./nlp/HybridTextSearch";
import { PrivacyCorpus } from "./nlp/PrivacyCorpus";
import { PlaceSource } from "tsproto/aware_proto/proto/Location";
import { Event_EventType } from "tsproto/aware_proto/proto/Event";
import { ETAQuery, EventQuery, HabitQuery, HabitType, PersonMatch, PlaceMatch, SonosPlaybackRequest, SonosPlaybackStatus, SonosPlayFavoriteRequest } from "tsproto/aware_proto/proto/Modules";
import { futureMainEvents, getEventsOfType, sortMainEvents } from "./EventUtils";
import { Habits } from "./nlp/Habits";
import { requestExpr } from "./nlp/Automations";
nlp.plugin(datePlugin);
nlp.plugin({
    tags: {
        AgentName: {
            isA: "Person",
        },
        UserName: {
            isA: "Person",
        },
        PlaceName: {
            isA: "Place"
        },
        TimeOfDay: {},
        TimeUnit: { isA: "Duration" },
        Activity: { isA: "Verb" },
    },
    words: {
        morning: "TimeOfDay",
        afternoon: "TimeOfDay",
        evening: "TimeOfDay",
        night: "TimeOfDay",
        year: "TimeUnit",
        month: "TimeUnit",
        week: "TimeUnit",
        day: "TimeUnit",
        hour: "TimeUnit",
        second: "TimeUnit",
        millisecond: "TimeUnit",
    },
});
export default class NLP {
    static nlp = nlp;
    static pipeline;
    static hybridTextSearch;
    static hts() {
        if (!NLP.hybridTextSearch) {
            const corpus = [
                ...PrivacyCorpus,
            ];
            NLP.hybridTextSearch = new HybridTextSearch(corpus);
        }
        return NLP.hybridTextSearch;
    }
    static doc(text, lexicon) {
        return nlp(text, lexicon);
    }
    static matchAgent(nlpDoc, env) {
        let view = nlpDoc.match("#AgentName");
        let displayName = view.text({ keepPunct: false });
        let agent = env.getAgentByDisplayName(displayName);
        if (!agent) {
            // This above sometimes drops text when there are spaces/numbers in the name
            const expr = `(${env.getAllAgents().map(agent => agent.getDisplayName()).join("|")})`;
            view = nlpDoc.match(expr);
            displayName = view.text({ keepPunct: false });
            agent = env.getAgentByDisplayName(displayName);
        }
        return [agent, view];
    }
    static matchAffirmative(doc) {
        const view = doc.match("(yes|yeah|sure|right|correct|good|fine|yea|yep|yup|certainly|indeed)");
        return !!view.text();
    }
    static sentiment(text) {
        return sentiment(text);
    }
    static getLexicon(userUid, env) {
        const lexicon = {};
        if (!env || !userUid)
            return lexicon;
        const userName = env.getUserDisplayName(userUid);
        if (userName) {
            lexicon[userName] = "UserName";
        }
        const agentName = env.getAgentByUserUid(userUid)?.getDisplayName();
        if (agentName) {
            lexicon[agentName] = "AgentName";
        }
        const user = env.getUserByUid(userUid);
        if (user?.membership) {
            for (const groupUid in user.membership.roles) {
                const group = env.getGroupByUid(groupUid);
                if (group) {
                    for (const groupMember of group.groupMembers) {
                        const userName = groupMember.getDisplayName();
                        if (userName) {
                            lexicon[userName] = "UserName";
                        }
                        const agentName = groupMember.agentInfo?.displayName;
                        if (agentName) {
                            lexicon[agentName] = "AgentName";
                        }
                    }
                }
            }
        }
        if (user?.userPlaces?.places?.length > 0) {
            for (const place of user.userPlaces.places) {
                for (const displayName of place.displayNames) {
                    lexicon[displayName.name] = "PlaceName";
                }
            }
        }
        return lexicon;
    }
    static getContextualStrings(env) {
        const strings = [];
        for (const user of env.getAllUsers()) {
            const displayName = user.getDisplayName();
            if (displayName) {
                strings.push(displayName);
            }
        }
        for (const agent of env.getAllAgents()) {
            const displayName = agent.getDisplayName();
            if (displayName) {
                strings.push(displayName);
            }
        }
        for (const user of env.getAllUsers()) {
            const places = user.userPlaces?.places;
            if (places) {
                for (const place of places) {
                    for (const displayName of place.displayNames) {
                        if (displayName.source === PlaceSource.UserInput) {
                            strings.push(displayName.name);
                        }
                    }
                }
            }
            const persons = user.userPersons?.persons;
            if (persons) {
                for (const person of persons) {
                    for (const name of person.names) {
                        strings.push(name.firstName);
                    }
                }
            }
        }
        for (const space of env.getAllSpaces()) {
            for (const name of space.awareSpace.names) {
                strings.push(name);
            }
            for (const region of space.awareSpace.regions) {
                for (const name of region.spaceNames) {
                    strings.push(name);
                }
            }
            const sonosState = space.getSonosState();
            if (sonosState) {
                for (const household of sonosState.households) {
                    for (const player of household.players) {
                        strings.push(player.playerName);
                    }
                    for (const favorite of household.favorites) {
                        strings.push(favorite.name);
                    }
                }
            }
        }
        env.interactionManager.getDeveloperContextualStrings().forEach(str => strings.push(str));
        return strings;
    }
    static getConjunctivePhrase(conj, phrases) {
        if (phrases.length === 0) {
            return "";
        }
        else if (phrases.length === 1) {
            return phrases[0];
        }
        else if (phrases.length === 2) {
            return `${phrases[0]} ${conj} ${phrases[1]}`;
        }
        else {
            let cp = "";
            for (let i = 0; i < phrases.length - 1; ++i) {
                cp += phrases[i] + ", ";
            }
            cp += `${conj} ${phrases[phrases.length - 1]}`;
            return cp;
        }
    }
    static getCountableNounPhrase(num, noun) {
        num = num || 0;
        const np = `${num} ${noun}`;
        const nouns = nlp(np).nouns();
        if (num === 1) {
            return nouns.toSingular().text();
        }
        return nouns.toPlural().text();
    }
    static capitalizeFirstLetter(text) {
        if (text?.length > 0) {
            return text.charAt(0).toUpperCase() + text.slice(1);
        }
        return undefined;
    }
    static camelCaseToWords(text) {
        return text.replace(/([a-z])([A-Z])/g, "$1 $2");
    }
    // This will try to match an agent name, and if it's not found, assume that the user's personal
    // agent is being addressed.
    static setDefaultAddressee(env, nlpDoc, intent) {
        const userUid = intent.speakerUid;
        const user = env.getUserByUid(userUid);
        const [agent] = NLP.matchAgent(nlpDoc, env);
        if (agent) {
            intent.addresseeUids = [agent.uid];
        }
        else {
            const agentUid = user.getAgentUid();
            if (agentUid) {
                intent.addresseeUids = [agentUid];
            }
        }
        return intent;
    }
    // This will try to match an agent name, and if it's not found, assume that all agents in the
    // context are being addressed.
    static setAllAgentAddressees(env, nlpDoc, intent) {
        const [agent] = NLP.matchAgent(nlpDoc, env);
        if (agent) {
            intent.addresseeUids = [agent.uid];
        }
        else {
            intent.addresseeUids = env.getAllAgents().map(agent => agent.uid);
        }
        return intent;
    }
    static pickOne(options) {
        return options[Math.floor(Math.random() * options.length)];
    }
    static stripPunctuation(text) {
        const punctuationless = text.replace(/[^\w\s\\']|_/g, "");
        const finalString = punctuationless.replace(/\s{2,}/g, " ");
        return finalString;
    }
    static capitalizeMonthsAndDays(text) {
        for (const day of DateUtils.DayNames) {
            text = text.replace(new RegExp(`${day}`, "i"), day);
        }
        for (const month of DateUtils.MonthNames) {
            text = text.replace(new RegExp(`${month}`, "i"), month);
        }
        return text;
    }
    static transformFirstToSecondPerson(text) {
        text = ` ${text} `;
        text = text.replace(" my ", " your ");
        text = text.replace(" me ", " you ");
        text = text.replace(" I ", " you ");
        text = text.trim();
        return text;
    }
    static getSpeakerName(env, intent) {
        let displayName = "";
        if (intent.speakerType === SpeakerType.User) {
            const user = env.getUserByUid(intent.speakerUid);
            displayName = user?.getDisplayName();
        }
        else if (intent.speakerType === SpeakerType.Agent) {
            const agent = env.getAgentByUid(intent.speakerUid);
            displayName = agent?.getDisplayName();
        }
        return displayName || "Unknown";
    }
    static prefixAddresseeName(text, addresseeName) {
        // Note this will be an issue for text that starts with a letter that needs to stay capitalized
        // such as I or a proper noun.
        text = text.charAt(0).toLowerCase() + text.slice(1);
        return `${addresseeName}, ${text}`;
    }
    static postfixAddresseeName(text, addresseeName) {
        const punctuationRegex = /([\p{P}]+)$/u;
        const punctuationMatch = text.match(punctuationRegex);
        let mainPart = text;
        let trailingPunctuation = "";
        if (punctuationMatch) {
            trailingPunctuation = punctuationMatch[0];
            mainPart = text.slice(0, -trailingPunctuation.length);
        }
        mainPart = mainPart.trim();
        return `${mainPart}, ${addresseeName}${trailingPunctuation}`;
    }
    static getGerund(verb) {
        const array = NLP.doc(verb).verbs().conjugate();
        if (array?.length > 0) {
            const forms = array[0];
            return forms?.Gerund;
        }
        return undefined;
    }
    static recognizeMood(text) {
        let matched = false;
        const doc = NLP.doc(text);
        if (doc.matchOne("I? am? (doing|feeling|feel)?").text()) {
            matched = true;
        }
        const sentiment = NLP.sentiment(text);
        let score = sentiment.score;
        if (Math.abs(sentiment.score) >= 2) {
            matched = true;
        }
        // Override for some common values - these are 0 by default
        if (!doc.matchOne("not").text() && doc.matchOne("(well|incredible)").text()) {
            score = Math.max(score, 3);
        }
        return [matched, score];
    }
    static isQuestion(input) {
        let doc;
        if (typeof input == "string") {
            doc = NLP.doc(input);
        }
        else {
            doc = input;
        }
        return doc.sentences().isQuestion().out("array").length > 0;
    }
    static getDuration(match) {
        let duration = match.duration;
        // Bug in compromise library; matches give 0 instead of 0.5 for the unit
        for (const timeUnit of TimeUnits) {
            if (match.text === `half an ${timeUnit}` || match.text === `half a ${timeUnit}`) {
                duration = { [timeUnit]: 0.5 };
            }
        }
        return duration;
    }
    // Ignores month and year fields
    static getDurationSeconds(duration) {
        let durationSecs = 0;
        if (duration.second)
            durationSecs += duration.second;
        if (duration.minute)
            durationSecs += 60 * duration.minute;
        if (duration.hour)
            durationSecs += 60 * 60 * duration.hour;
        if (duration.day)
            durationSecs += 24 * 60 * 60 * duration.day;
        if (duration.week)
            durationSecs += 7 * 24 * 60 * 60 * duration.week;
        return durationSecs;
    }
    static filterTerms(text, terms) {
        if (!text)
            return text;
        return text.split(/\s+/).filter(token => !terms.includes(token)).join(" ");
    }
    static matchPlaces(inputText, user) {
        const places = [];
        const matches = [];
        if (inputText && user?.userPlaces?.places?.length > 0) {
            const inputTextTrimmed = NLP.filterTerms(inputText, ["the"]);
            for (const place of user.userPlaces.places) {
                for (const displayName of place.displayNames) {
                    const placeMatch = PlaceMatch.create({
                        placeUid: place.placeUid,
                        userUid: user.uid,
                        placeName: displayName.name
                    });
                    const doc = NLP.doc(displayName.name);
                    const text = doc.match("the").text() ? inputText : inputTextTrimmed;
                    const match = NLP.doc(displayName.name).match(text);
                    if (match.text()) {
                        placeMatch.placeText = match.text();
                        places.push(place);
                        matches.push(placeMatch);
                        break;
                    }
                }
            }
        }
        return [places, matches];
    }
    static matchPersons(inputText, user) {
        const persons = [];
        const matches = [];
        if (inputText && user?.userPersons?.persons?.length > 0) {
            for (const person of user.userPersons.persons) {
                for (const name of person.names) {
                    const personMatch = PersonMatch.create({
                        personUid: person.personUid,
                        userUid: user.uid,
                        personName: name.firstName
                    });
                    const doc = NLP.doc(`${name.firstName} ${name.lastName}?`);
                    const match = doc.match(inputText);
                    if (match.text()) {
                        personMatch.personText = match.text();
                        persons.push(person);
                        matches.push(personMatch);
                        break;
                    }
                }
            }
        }
        return [persons, matches];
    }
    static recognizeHabitQuery(doc, user) {
        for (const habit of Object.values(Habits)) {
            const [matched, entityName] = habit.match(doc);
            const isPlaceHabit = HabitType[habit.habitType].includes("Place");
            if (matched && isPlaceHabit) {
                const [, matches] = NLP.matchPlaces(entityName, user);
                const habitQuery = HabitQuery.create({
                    habitType: habit.habitType,
                    placeText: entityName,
                    placeMatches: matches
                });
                return [true, habitQuery];
            }
            const isPersonHabit = HabitType[habit.habitType].includes("Person");
            if (matched && isPersonHabit) {
                const [, matches] = NLP.matchPersons(entityName, user);
                const habitQuery = HabitQuery.create({
                    habitType: habit.habitType,
                    personText: entityName,
                    personMatches: matches
                });
                return [true, habitQuery];
            }
        }
        return [false];
    }
    static matchEvents(eventName, userSchedule, future) {
        const matchedMainEvents = [];
        if (userSchedule?.mainEvents) {
            let mainEvents = Object.values(userSchedule?.mainEvents);
            if (future) {
                mainEvents = futureMainEvents(mainEvents);
                sortMainEvents(mainEvents);
            }
            for (const me of mainEvents) {
                const testEventName = eventName.toLowerCase().replace(/[^\w\s]|_/g, "");
                const mainEventName = me.event.eventName.toLowerCase().replace(/[^\w\s]|_/g, "");
                if (mainEventName === testEventName ||
                    nlp.lazy(mainEventName, testEventName).text() ||
                    nlp.lazy(testEventName, mainEventName).text() ||
                    NLP.doc(mainEventName).match(`~${testEventName}~`, null, { fuzzy: 0.65 }).text() ||
                    NLP.doc(testEventName).match(`~${mainEventName}~`, null, { fuzzy: 0.65 }).text()) {
                    matchedMainEvents.push(me);
                }
            }
        }
        return matchedMainEvents;
    }
    static recognizeEventQuery(doc, userSchedule) {
        if (!userSchedule?.mainEvents) {
            return [false];
        }
        const mainEvents = futureMainEvents(Object.values(userSchedule.mainEvents));
        sortMainEvents(mainEvents);
        const whenExpr = "(when|what time)";
        const prepExpr = "(should I|do I need to)? (get ready|prepare) for?";
        const transitExpr = "(should I|do I need to)? (leave|depart|walk|drive) (for|to)?";
        // Use map because of optional enum keys
        const exprs = new Map();
        exprs.set(Event_EventType.CalendarEvent, `${whenExpr} (is|do I have) [.*]`);
        exprs.set(Event_EventType.Preparation, `${whenExpr} ${prepExpr} [*]?`);
        exprs.set(Event_EventType.Transit, `${whenExpr} ${transitExpr} [*]?`);
        for (const [eventType, expr] of exprs) {
            const match = doc.match(expr);
            if (match.text()) {
                const group0Text = match.groups("0").text();
                let mainEvent;
                if (group0Text) {
                    const matchedEvents = NLP.matchEvents(group0Text, userSchedule, true);
                    if (matchedEvents.length > 0) {
                        mainEvent = matchedEvents[0];
                    }
                    if (!mainEvent) {
                        // Check for "my next appointment"
                        if (NLP.doc(group0Text).match("(my|the)? next (appointment|event|meeting|thing)").text()) {
                            mainEvent = mainEvents[0];
                        }
                    }
                }
                else {
                    if (mainEvents.length > 0) {
                        if (eventType === Event_EventType.Transit) {
                            // For transit, get the next event that has transit options
                            const mainEventWithTransit = mainEvents.find(me => getEventsOfType(me, Event_EventType.Transit).length > 0);
                            if (mainEventWithTransit) {
                                mainEvent = mainEventWithTransit;
                            }
                        }
                        else {
                            mainEvent = mainEvents[0];
                        }
                    }
                }
                const eventQuery = EventQuery.create({
                    eventType: eventType,
                    mainEventUid: mainEvent?.event?.mainEventUid || "",
                    eventText: group0Text
                });
                return [true, eventQuery];
            }
        }
        return [false];
    }
    static recognizeETAQuery(doc, speakerUid, env) {
        // Use map because of optional enum keys
        const expr = "(when|what time) (is|will)? [(I|we|#UserName+)] next? (be|get|arrive) (to|at)? [.*]";
        const match = doc.match(expr);
        if (match.text()) {
            const group0Text = match.groups("0").text();
            const group1Text = match.groups("1").text();
            let user;
            if (NLP.doc(group0Text).match("I|we").text()) {
                user = env.getUserByUid(speakerUid);
            }
            else {
                user = env?.getUserByDisplayName(group0Text);
            }
            if (user) {
                const [, matches] = NLP.matchPlaces(group1Text, user);
                const etaQuery = ETAQuery.create({
                    userUid: user.uid,
                    placeText: group1Text,
                    placeMatches: matches
                });
                return [true, etaQuery];
            }
        }
        return [false];
    }
    static segmentLocation(location) {
        if (!location) {
            return ["", ""];
        }
        const doc = NLP.doc(location);
        const result = doc.text().match(AddrRegex);
        let tagIndex = result?.index;
        if (!(tagIndex > -1)) {
            const tags = ["#Place"];
            for (const tag of tags) {
                if (tagIndex > -1)
                    break;
                const match = doc.match(tag);
                if (match.text()) {
                    tagIndex = location.indexOf(match.text());
                    break;
                }
            }
        }
        if (tagIndex > -1) {
            if (tagIndex === 0) {
                return ["", location];
            }
            // Find the last comma before the match
            let commaIndex = -1;
            for (let i = tagIndex; i > -1; --i) {
                if (location[i] === ",") {
                    commaIndex = i;
                    break;
                }
            }
            if (commaIndex > 0) {
                const name = location.substring(0, commaIndex).trim();
                const address = location.substring(commaIndex + 1).trim();
                return [name, address];
            }
        }
        return [location, ""];
    }
    static matchSonosFavorite(inputText, sonosState) {
        if (!sonosState)
            return undefined;
        const matchedNames = [];
        const matchedFavorites = [];
        for (const household of sonosState.households) {
            for (const favorite of household.favorites) {
                const expr = favorite.name.split(" ").map(v => `${v}?`).join(" ");
                let matchedText = NLP.doc(inputText).match(expr).text();
                if (matchedText) {
                    matchedNames.push(matchedText);
                    matchedFavorites.push(favorite);
                }
                const nameNoPunc = favorite.name.replace(/[^\w\s]/g, "");
                if (nameNoPunc === favorite.name)
                    continue;
                const exprNoPunc = nameNoPunc.split(" ").map(v => `${v}?`).join(" ");
                matchedText = NLP.doc(inputText).match(exprNoPunc).text();
                if (matchedText) {
                    matchedNames.push(matchedText);
                    matchedFavorites.push(favorite);
                }
            }
        }
        if (matchedNames.length > 0) {
            let bestName = "";
            let bestFavorite;
            for (let i = 0; i < matchedNames.length; ++i) {
                const name = matchedNames[i];
                if (name.length > bestName.length) {
                    bestName = name;
                    bestFavorite = matchedFavorites[i];
                }
            }
            return bestFavorite;
        }
        return undefined;
    }
    static matchSonosPlayer(inputText, space) {
        const awareSpaceState = space.awareSpaceState;
        const sonosState = awareSpaceState?.sonosState;
        if (!sonosState)
            return [undefined, undefined];
        // Check Sonos player names
        const household = space.getSonosHousehold();
        for (const player of household.players) {
            const text = nlp.lazy(inputText, player.playerName).text();
            if (text) {
                const group = space.getSonosGroupWithPlayerId(player.playerId);
                return [player, group];
            }
        }
        // Check AwareSpace names
        for (const region of space.awareSpace.regions) {
            if (!(region.sonosPlayerIds?.length > 0))
                continue;
            const playerId = region.sonosPlayerIds[0];
            const player = space.getSonosPlayerById(playerId);
            const group = space.getSonosGroupWithPlayerId(playerId);
            for (const spaceName of region.spaceNames) {
                const text = nlp.lazy(inputText, spaceName).text();
                if (text) {
                    return [player, group];
                }
            }
        }
        return [undefined, undefined];
    }
    static recognizeMusicRequest(doc, space) {
        const playVerbs = ["play", "resume", "put on"];
        const stopVerbs = ["stop", "pause"];
        const playExpr = requestExpr(`(${playVerbs.join("|")})`);
        const stopExpr = requestExpr(`(${stopVerbs.join("|")})`);
        const playMatch = doc.match(playExpr);
        const stopMatch = doc.match(stopExpr);
        const sonosFavorite = NLP.matchSonosFavorite(doc.text(), space?.awareSpaceState?.sonosState);
        const [, group] = NLP.matchSonosPlayer(doc.text(), space);
        if (stopMatch.text() && doc.has("music")) {
            const playbackRequest = SonosPlaybackRequest.create();
            playbackRequest.playbackStatus = SonosPlaybackStatus.StoppedPlaybackStatus;
            playbackRequest.groupId = group?.groupId || "";
            return [true, playbackRequest, undefined];
        }
        else if (playMatch.text()) {
            if (sonosFavorite) {
                const favoriteRequest = SonosPlayFavoriteRequest.create();
                favoriteRequest.favoriteId = sonosFavorite.id;
                favoriteRequest.groupId = group?.groupId || "";
                return [true, undefined, favoriteRequest];
            }
            else if (doc.has("music")) {
                const playbackRequest = SonosPlaybackRequest.create();
                playbackRequest.playbackStatus = SonosPlaybackStatus.PlayingPlaybackStatus;
                playbackRequest.groupId = group?.groupId || "";
                return [true, playbackRequest, undefined];
            }
        }
        return [false];
    }
    // Compromise match API offers capture groups and wildcards, but there is an issue where the
    // group doesn't include the last one or more tokens of the wildcard:
    // https://github.com/spencermountain/compromise/issues/1139
    // For now we work around the bug by replacing wildcards with all tagss possible to be matched.
    static replaceWildcards(expr, excludeTags) {
        const tags = AllTags.filter(tag => !excludeTags?.includes(tag));
        const groupExpr = `(${tags.join("|")})+`;
        expr = expr.replace(/\.\*/g, groupExpr);
        expr = expr.replace(/\*/g, groupExpr);
        return expr;
    }
}
// Top-level tags. https://observablehq.com/@spencermountain/compromise-tags
const AllTags = [
    "#Noun", "#Verb", "#Value", "#Date", "#Adjective", "#Contraction", "#Adverb", "#Currency",
    "#Determiner", "#Conjunction", "#Preposition", "#QuestionWord", "#Pronoun", "#Expression",
    "#Abbreviation", "#Url", "#HashTag", "#PhoneNumber", "#AtMention", "#Emoji", "#Emoticon",
    "#Email", "#Auxiliary", "#Negative", "#Acronym"
];
// Not exhaustive, but the ones we are focused on supporting.
const TimeUnits = ["year", "month", "week", "day", "hour", "minute", "second", "millisecond"];
const AddrRegex = new RegExp("\\b\\d+\\s(?:[A-Za-z0-9]+\\s)+" +
    "(?:Street|St|Avenue|Ave|Boulevard|Blvd|Road|Rd|Lane|Ln|Drive|Dr|" +
    "Court|Ct|Place|Pl|Terrace|Ter|Way|Square|Sq)\\b" +
    "(?:\\s.*)?", "i");
